<template>
  <sFormItem
    ref="formItem"
    :attr-order="field.order"
    class="additional-field"
    :data-qa="`additional-field__${typeId}_${field.id}`"
    :error="error"
    :label="label && typeId != 'CHECKBOX' ? field.caption : ''"
    :prop="validationProp"
    :rules="rules">
    <span
      v-if="$slots.default"
      slot="label"><slot/></span>
    <sInput
      v-if=" ['TEXT', 'STRING', 'URL', 'EMAIL', 'PHONE'].includes(typeId) "
      :id="field.id + '-additional-field'"
      class="additional-field__input"
      clearable
      data-qa="additional-field__input"
      :disabled="loading"
      :max-length="4000"
      max-rows="2"
      :placeholder="input_placeholder"
      rows="1"
      :type="inputType"
      :value.sync="modelFieldInputMutable"/>
    <sCheckbox
      v-else-if="typeId == 'CHECKBOX'"
      :id="field.id + '-additional-field'"
      v-model="modelFieldInputMutable"
      class="additional-field__checkbox"
      data-qa="additional-field__checkbox"
      :disabled="loading"
      :false-label="'false'"
      :true-label="'true'">
      <span
        v-if="required"
        class="h5-headline secondary__red--text">*</span>
      {{ field.caption }}
    </sCheckbox>
    <sSelect
      v-if="typeId == 'DROPDOWN'"
      :id="field.id + '-additional-field'"
      class="width-100"
      clearable
      data-qa="additional-field__dropdown"
      :disabled="loading"
      :placeholder="$t('Common.Select')"
      popper-class="additional-field__dropdown"
      :value.sync="modelFieldInputMutable">
      <sOption
        v-for="option in options"
        :key="`option-${option.id || option}`"
        class="px-8"
        :data-qa="`option-${option.text || option}`"
        :disabled="loading"
        :label="option.value || option"
        :value="option.value || option.id || option"/>
    </sSelect>
    <template v-else-if="['INTEGER', 'DECIMAL'].includes(typeId)">
      <sInputNumber
        :id="field.id + '-additional-field'"
        ref="number_value"
        class="additional-field__number input"
        clearable
        data-qa="additional-field__number"
        :disabled="loading"
        :minus="true"
        :placeholder="typeId == 'INTEGER' ? '0' : '0.00'"
        :precision="typeId == 'INTEGER' ? 0 : decimals"
        :use-thousand-separator="true"
        :value.sync="modelFieldInputMutable"
        @action="check"/>
    </template>

    <template v-else-if="typeId == 'DATE_TIME'">
      <DateMenu
        :id="field.id + '-additional-field'"
        ref="menu_date_end"
        :change-date="changePropDate"
        class="additional-field__date"
        clearable
        custom_text_class="additional-field__date width-100"
        data-qa="additional-field__date"
        :disabled="loading"
        :error="$refs.formItem ? $refs.formItem.validateState === 'error' : false"
        :value.sync="modelFieldInputMutable"/>
    </template>
  </sFormItem>
</template>

<script>
import DateMenu from 'components/common/inputs/DateMenu.vue'
import { additionalFieldsMixin } from 'mixins'
import { modalMixin } from 'mixins'

export default {
  name: 'additionalFieldInput',
  mixins: [additionalFieldsMixin, modalMixin],
  props: {
    loading: {
      type: Boolean
    },
    field: {
      type: Object,
      required: true
    },
    label: {
      type: Boolean,
      default: true
    },
    validationProp: {
      type: String,
    },
    error: {
      type: String
    },
    rules: {
      type: [Array, Object],
    },
  },
  computed: {
    modelFieldInputMutable: {
      get() {
        if (['INTEGER', 'DECIMAL'].includes(this.typeId)) {
          if (!this.field_sync.value || typeof this.field_sync.value === 'undefined') {
            return 0
          }
          return parseFloat(this.field_sync.value)
        }
        return this.field_sync.value
      },
      set(value) {
        this.$emit('update:value', value)
        this.field_sync.value = value
      }
    },
    field_sync: {
      get() {
        return this.field
      },
      set(value) {
        this.$emit('update:field', value)
        this.$emit('input', value)
      }
    },
    input_placeholder() {
      if (this.typeId == 'EMAIL' ) {
        return this.$t('Common.MailAddress')
      } else if ( this.typeId == 'URL') {
        return this.$t('Common.EnterLink')
      } else if ( this.typeId == 'PHONE') {
        return '+ 380 (––) ––– –– ––'
      } else {
        return this.$t('Common.EnterText')
      }
    },
    typeId() {
      return this.field.type?.typeId || this.field.typeId || this.additional_types?.find(additionalType => additionalType.typeId == this.field.typeId)?.typeId
    },
    options() {
      if ( this.typeId != 'DROPDOWN' || !this.field.settingValues.length) {
        return []
      } else {
        return this.field.settingValues
          .filter(opt => opt.settingType == 'SELECT_OPTION')
          .map(opt => ({value: opt.value, id: opt.id}))
      }
    },
    required() {
      return this.field?.settingValues?.some(set => set?.settingType == 'REQUIRED' && eval(set?.value))
    },
    decimals() {
      const item = this.field?.settingValues?.find(set => set?.settingType == 'DECIMALS')
      if (item) {
        return parseInt(item.value)
      }
      return 0
    },
    inputType() {
      if ( this.typeId == 'PHONE') {
        return 'tel'
      } else if ( this.typeId == 'URL') {
        return 'url'
      } else if ( this.typeId == 'EMAIL') {
        return 'email'
      } else if (this.typeId == 'TEXT') {
        return 'textarea'
      } else {
        return 'text'
      }
    },
  },
  methods: {
    validate() {
      this.$children.forEach(child => {
        if (typeof child.validate === 'function') {
          child.validate()
        }
      })
    },
    changePropDate(date) {
      this.$refs.menu_date_end?.$refs.menu?.save(date)
    },
    check(text) {
      this.sliceText(
        text,
        this.$refs.number_value?.$refs?.input,
        this.typeId == 'INTEGER' ? 0 : 8,
        (val) => {
          this.modelFieldInputMutable = +this.$_formatValue(val || 0)
        },
        9 )
      this.$refs.formItem.$emit.apply(this.$refs.formItem, ['s.form.change'].concat(this.modelFieldInputMutable))
    },
  },
  components: {
    DateMenu,
  }
}
</script>
<style lang="scss" scoped>
 .additional-field{
  max-width: 500px;
  & ::v-deep &__date{
    max-width: 100%;
    .date-chip {
      width: 100%;
    }
  }
}

</style>
